/**
 * Hermes Input - Themed text input with focus animations
 *
 * Features:
 * - Animated focus state with color transition
 * - Error state with color change
 * - Floating label animation
 * - Full accessibility support (ARIA labels)
 * - Integrates with Hermes theme colors
 */

import React from 'react';
import { TextField, TextFieldProps, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export interface HermesInputProps extends Omit<TextFieldProps, 'variant'> {
  /**
   * Input variant style
   * @default 'outlined'
   */
  variant?: 'outlined' | 'filled' | 'standard';

  /**
   * Whether to show animated focus ring
   * @default true
   */
  animatedFocus?: boolean;

  /**
   * Icon to display at start of input
   */
  startIcon?: React.ReactNode;

  /**
   * Icon to display at end of input
   */
  endIcon?: React.ReactNode;
}

// Styled TextField with enhanced focus animations
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'animatedFocus',
})<{ animatedFocus?: boolean }>(({ theme, animatedFocus = true }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
    backgroundColor: theme.palette.background.paper,

    '& fieldset': {
      borderWidth: 2,
      borderColor: theme.palette.grey[300],
      transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
    },

    '&:hover fieldset': {
      borderColor: theme.palette.primary.light,
    },

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      ...(animatedFocus && {
        boxShadow: `0 0 0 4px ${theme.palette.primary.main}20`,
      }),
    },

    '&.Mui-error fieldset': {
      borderColor: theme.palette.error.main,
    },

    '&.Mui-error.Mui-focused fieldset': {
      borderColor: theme.palette.error.main,
      ...(animatedFocus && {
        boxShadow: `0 0 0 4px ${theme.palette.error.main}20`,
      }),
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      opacity: 0.7,
    },
  },

  '& .MuiInputLabel-root': {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontWeight: 500,
    transition: `all ${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeInOut}`,

    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },

    '&.Mui-error': {
      color: theme.palette.error.main,
    },
  },

  '& .MuiFormHelperText-root': {
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
    lineHeight: 1.5,
  },

  // Filled variant styles
  '& .MuiFilledInput-root': {
    borderRadius: 8,
    backgroundColor: theme.palette.grey[100],
    transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,

    '&:hover': {
      backgroundColor: theme.palette.grey[200],
    },

    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      ...(animatedFocus && {
        boxShadow: `0 0 0 4px ${theme.palette.primary.main}20`,
      }),
    },
  },

  // Standard variant styles
  '& .MuiInput-root': {
    '&:before': {
      borderBottomWidth: 2,
      transition: `all ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeInOut}`,
    },

    '&:hover:not(.Mui-disabled):before': {
      borderBottomWidth: 2,
      borderBottomColor: theme.palette.primary.light,
    },

    '&.Mui-focused:after': {
      borderBottomWidth: 2,
    },
  },
}));

// Error message animation variants
const errorVariants = {
  initial: {
    opacity: 0,
    y: -8,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};

export const Input = React.forwardRef<HTMLDivElement, HermesInputProps>(
  (
    {
      animatedFocus = true,
      startIcon,
      endIcon,
      error,
      helperText,
      variant = 'outlined',
      ...props
    },
    ref
  ) => {
    // Build InputProps with icons
    const inputProps = {
      ...(startIcon && {
        startAdornment: (
          <InputAdornment position="start">
            {startIcon}
          </InputAdornment>
        ),
      }),
      ...(endIcon && {
        endAdornment: (
          <InputAdornment position="end">
            {endIcon}
          </InputAdornment>
        ),
      }),
      // Show error icon automatically if error state
      ...(error && !endIcon && {
        endAdornment: (
          <InputAdornment position="end">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring' as const, stiffness: 400, damping: 20 }}
            >
              <ErrorOutlineIcon color="error" fontSize="small" />
            </motion.div>
          </InputAdornment>
        ),
      }),
    };

    return (
      <StyledTextField
        ref={ref}
        variant={variant}
        animatedFocus={animatedFocus}
        error={error}
        InputProps={inputProps}
        helperText={
          <AnimatePresence mode="wait">
            {helperText && (
              <motion.span
                key={String(helperText)}
                variants={errorVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                {helperText}
              </motion.span>
            )}
          </AnimatePresence>
        }
        {...props}
      />
    );
  }
);

Input.displayName = 'HermesInput';

export default Input;
